import { t } from '@hefe/i18n';
import {
  BasketballEvent,
  BasketballEventStatus,
  BasketballEventType,
  BasketballEventUserRolePlayerCoach,
  BasketballEventUserRoleType,
} from '@hefe/models';
import { ModalsSlice } from '@hefe/store';
import { BasketballEventColor } from '@hefe/ui';
import { formatTime } from '@hefe/utils';
import { Avatar, Col, Row, Space, Tooltip, Typography } from 'antd';
import { useMemo } from 'react';
import { AiOutlineCar, AiOutlineHome } from 'react-icons/ai';
import {
  FaCheck,
  FaCompressArrowsAlt,
  FaQuestion,
  FaTimes,
} from 'react-icons/fa';
import { FiUsers } from 'react-icons/fi';
import { GoLocation } from 'react-icons/go';
import { useAppDispatch } from '../../../../store/store';
import UserAvatar from '../../../core/components/user/user-avatar/user-avatar/user-avatar.component';
import EventIconComponent from '../event-icon/event-icon.component';
import EventReplyComponent from '../event-reply/event-reply.component';
import EventRoleIconComponent from '../event-role-icon/event-role-icon.component';
import styles from './user-event-list-item.module.scss';

export interface UserEventListItemProps {
  event: BasketballEvent;
}

export default function UserEventListItem(props: UserEventListItemProps) {
  const dispatch = useAppDispatch();
  const { event } = props;

  const onListItemClick = () => {
    if (event.type === BasketballEventType.GAME) {
      dispatch(ModalsSlice.modalsActions.openGameModal({ id: event.id }));
    } else if (event.type === BasketballEventType.PRACTICE) {
      dispatch(ModalsSlice.modalsActions.openPracticeModal({ id: event.id }));
    } else if (event.type === BasketballEventType.EVENT) {
      dispatch(ModalsSlice.modalsActions.openEventModal({ id: event.id }));
    }
  };

  const eventTitle = useMemo(() => {
    switch (event.type) {
      case BasketballEventType.GAME:
        return event.description;
      case BasketballEventType.PRACTICE:
        return t('practice');
      case BasketballEventType.EVENT:
        return event.description;
      default:
        return event.description;
    }
  }, [event]);

  const renderRoles = () => {
    const userData = event.user_data;
    const usersOutput: React.ReactElement[] = [];
    for (const user of userData) {
      const roles: React.ReactElement[] = [];
      for (const role of user.roles) {
        if (
          [
            BasketballEventUserRoleType.PLAYER,
            BasketballEventUserRoleType.COACH,
            BasketballEventUserRoleType.PARTICIPANT,
          ].includes(role.type)
        ) {
          continue;
        }

        roles.push(
          <EventRoleIconComponent
            key={user.user?.id + role.type}
            eventType={role.type}
            asAvatar
            withTooltip
            tooltipText={
              role.type === BasketballEventUserRoleType.TASK
                ? role.name
                : undefined
            }
          />
        );
      }
      const maxCount = roles.length === 2 ? 2 : 1;
      if (event.contains_children_data) {
        usersOutput.push(
          <Avatar.Group
            key={user.user.id}
            size="large"
            maxCount={maxCount + 1}
            maxStyle={{ borderRadius: 5 }}
          >
            <UserAvatar
              user={user.user}
              withTooltip
              shape="square"
              size="large"
            />
            {roles}
          </Avatar.Group>
        );
      } else {
        usersOutput.push(
          <Avatar.Group
            key={'single_user'}
            size="large"
            maxCount={maxCount}
            maxStyle={{ borderRadius: 5 }}
          >
            {roles}
          </Avatar.Group>
        );
      }
    }

    return (
      <Space style={{ alignItems: 'flex-start' }} direction="vertical">
        <EventIconComponent eventType={event.type} />
        {usersOutput}
      </Space>
    );
  };

  const renderTeamReplies = () => {
    const returnValue: React.ReactElement[] = [];

    const teamsWithReplies = event.teams.filter(
      (team) => team.replies !== undefined
    );

    if (teamsWithReplies.length === 0) {
      return null;
    }

    for (const team of teamsWithReplies) {
      returnValue.push(
        <Space direction="vertical" size={0} align="center">
          <Typography.Text strong>{team.name}</Typography.Text>
          <Typography.Text>
            <FaCheck size={10} /> {team.replies?.yes} / <FaTimes size={10} />{' '}
            {team.replies?.no} / <FaQuestion size={10} />{' '}
            {team.replies?.unknown}
          </Typography.Text>
        </Space>
      );
    }

    return <Space direction="vertical">{returnValue}</Space>;
  };

  const renderReplyStatus = () => {
    const userElements: React.ReactElement[] = [];
    // If event contains children data -> show replies together with user avatar
    for (const user of event.user_data) {
      for (const role of user.roles) {
        if (
          role.type === BasketballEventUserRoleType.PLAYER ||
          role.type === BasketballEventUserRoleType.PARTICIPANT
        ) {
          if (event.contains_children_data) {
            userElements.push(
              <Avatar.Group
                key={user.user.id + role.type}
                size="small"
                style={{ cursor: 'pointer' }}
              >
                <UserAvatar user={user.user} withTooltip />
                <EventReplyComponent
                  size="small"
                  event={props.event}
                  eventRole={role as BasketballEventUserRolePlayerCoach}
                  userId={user.user.id}
                />
              </Avatar.Group>
            );
          } else {
            userElements.push(
              <EventReplyComponent
                size="small"
                event={props.event}
                eventRole={role as BasketballEventUserRolePlayerCoach}
                userId={user.user.id}
              />
            );
          }
        }
      }
    }

    return <Space direction="vertical">{userElements}</Space>;
  };

  const renderTeamNames = () => {
    if (event.teams && event.teams.length > 0) {
      const teamNames = event.teams.map((t) => t.name).join(', ');
      return (
        <Space>
          <FiUsers size={16} style={{ marginTop: 5 }} />
          <Typography.Text style={{ fontSize: 12 }}>
            {teamNames}
          </Typography.Text>
        </Space>
      );
    }

    return null;
  };

  const renderLocation = () => {
    let Icon = GoLocation;
    if (event.type === BasketballEventType.GAME) {
      Icon = AiOutlineCar;
      if (event.home) {
        Icon = AiOutlineHome;
      }
    }

    return (
      <Space align="center">
        {Icon && <Icon size={16} style={{ marginTop: 5 }} />}
        <Typography.Text>{event.location}</Typography.Text>
      </Space>
    );
  };

  const renderMeetingPoint = () => {
    const meetingPoints: React.ReactElement[] = [];
    for (const team of event.teams) {
      const time = team.meet_at?.meet_at_time || '--:--';
      const location = team.meet_at?.meet_at_location || t('tbd');
      meetingPoints.push(
        <Space key={team.id} align="center">
          <Typography.Text>{team.name}</Typography.Text>
          <Typography.Text>{time}</Typography.Text>
          <Typography.Text>|</Typography.Text>
          <Typography.Text>{location}</Typography.Text>
        </Space>
      );
    }

    if (meetingPoints.length === 0) {
      return;
    }

    return (
      <Space>
        <Tooltip title={t('meet_at')}>
          <FaCompressArrowsAlt size={16} style={{ marginTop: 5 }} />
        </Tooltip>
        <Space direction="vertical">{meetingPoints}</Space>
      </Space>
    );
  };

  const canceledClass =
    event.status === BasketballEventStatus.CANCELED ? styles['canceled'] : '';
  const borderLeftColor =
    event.status === BasketballEventStatus.CANCELED
      ? BasketballEventColor.CANCELED
      : event.type === BasketballEventType.GAME
      ? BasketballEventColor.GAME
      : event.type === BasketballEventType.PRACTICE
      ? BasketballEventColor.PRACTICE
      : BasketballEventColor.EVENT;
  /*******************************************************************************************
   *                            RENDER FUNCTION
   * *****************************************************************************************
   */
  return (
    <div
      className={`${styles['container']} ${canceledClass}`}
      style={{ borderLeftColor }}
      onClick={() => onListItemClick()}
    >
      <Row
        justify="space-between"
        align="middle"
        gutter={[{ xs: 20 }, { xs: 30, sm: 20, md: 0 }]}
      >
        <Col xs={24} sm={12} xl={6} style={{ textAlign: 'center' }}>
          <Typography.Title level={5}>
            {event.status === BasketballEventStatus.CANCELED
              ? t('canceled').toUpperCase()
              : formatTime(event.date)}
          </Typography.Title>
          {renderRoles()}
        </Col>
        <Col xs={24} sm={12} xl={10}>
          <Space direction="vertical" size={0}>
            <Typography.Text strong style={{ fontSize: 16 }}>
              {eventTitle}
            </Typography.Text>
            {renderTeamNames()}
            {renderLocation()}
            {renderMeetingPoint()}
          </Space>
        </Col>
        <Col xs={12} xl={4} style={{ textAlign: 'center' }}>
          {renderTeamReplies()}
        </Col>
        <Col xs={12} xl={4} style={{ textAlign: 'center' }}>
          {renderReplyStatus()}
        </Col>
      </Row>
    </div>
  );
}
